import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { supabase } from "../../supabaseClient"
import { Card, CardContent } from "../../components/ui/card"
import { getActiveYear } from "../../utils/activeYear"
import { clearScoringConfigCache } from "../../utils/scoring"

interface RoundConfig {
  id?: string
  year: number
  round: number
  round_name: string
  multiplier: number
}

const DEFAULT_ROUNDS: Omit<RoundConfig, 'id' | 'year'>[] = [
  { round: 1, round_name: 'Round of 64', multiplier: 1 },
  { round: 2, round_name: 'Round of 32', multiplier: 1 },
  { round: 3, round_name: 'Sweet 16', multiplier: 2 },
  { round: 4, round_name: 'Elite 8', multiplier: 2 },
  { round: 5, round_name: 'Final Four', multiplier: 3 },
  { round: 6, round_name: 'Championship', multiplier: 3 },
]

export default function ScoringConfig() {
  const [configs, setConfigs] = useState<RoundConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeYear, setActiveYearState] = useState<number | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    loadActiveYear()
  }, [])

  async function loadActiveYear() {
    const year = await getActiveYear()
    setActiveYearState(year)
    loadConfigs(year)
  }

  async function loadConfigs(year: number) {
    setLoading(true)
    
    const { data, error } = await supabase
      .from('scoring_config')
      .select('*')
      .eq('year', year)
      .order('round')

    if (error) {
      console.error('Error loading scoring config:', error)
    }

    if (data && data.length > 0) {
      setConfigs(data)
    } else {
      // Initialize with defaults if none exist
      setConfigs(DEFAULT_ROUNDS.map(r => ({ ...r, year })))
    }
    
    setLoading(false)
    setHasChanges(false)
  }

  function updateMultiplier(round: number, multiplier: number) {
    setConfigs(configs.map(c => 
      c.round === round ? { ...c, multiplier } : c
    ))
    setHasChanges(true)
  }

  async function saveConfigs() {
    setSaving(true)

    // Upsert all configs
    const { error } = await supabase
      .from('scoring_config')
      .upsert(
        configs.map(c => ({
          year: activeYear,
          round: c.round,
          round_name: c.round_name,
          multiplier: c.multiplier
        })),
        { onConflict: 'year,round' }
      )

    if (error) {
      console.error('Error saving scoring config:', error)
      alert('Error saving: ' + error.message)
    } else {
      clearScoringConfigCache() // Clear cache so new values are used
      setHasChanges(false)
      alert('Scoring configuration saved!')
    }

    setSaving(false)
  }

  async function resetToDefaults() {
    if (!activeYear) return
    if (!window.confirm('Reset all multipliers to default values?')) return
    
    setConfigs(DEFAULT_ROUNDS.map(r => ({ ...r, year: activeYear })))
    setHasChanges(true)
  }

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh] text-gray-500">Loading...</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/admin" className="text-gray-400 hover:text-gray-600">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Scoring Configuration</h1>
          <p className="text-gray-500">Set round multipliers for <span className="font-semibold text-[#F15A22]">{activeYear}</span></p>
        </div>
        {hasChanges && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-amber-600">Unsaved changes</span>
            <button
              onClick={saveConfigs}
              disabled={saving}
              className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>

      {/* Formula - minimal */}
      <p className="text-sm text-gray-500">
        <span className="font-medium text-gray-700">Points = Seed × Multiplier</span> — picking upsets earns more points
      </p>

      {/* Round Configuration - Simple Table */}
      <Card className="bg-white border-0 shadow-sm">
        <CardContent className="p-0">
          <div className="divide-y divide-gray-100">
            {configs.map((config) => (
              <div key={config.round} className="flex items-center justify-between px-4 py-3">
                {/* Round Name */}
                <div className="font-medium text-gray-800">{config.round_name}</div>

                {/* Multiplier Controls */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateMultiplier(config.round, Math.max(1, config.multiplier - 1))}
                    className="w-8 h-8 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center justify-center font-bold"
                  >
                    −
                  </button>
                  <span className="w-8 text-center text-lg font-bold text-[#1e3a5f]">{config.multiplier}×</span>
                  <button
                    onClick={() => updateMultiplier(config.round, config.multiplier + 1)}
                    className="w-8 h-8 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center justify-center font-bold"
                  >
                    +
                  </button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-between items-center">
        <button
          onClick={resetToDefaults}
          className="text-sm text-gray-500 hover:text-gray-700 underline"
        >
          Reset to Defaults
        </button>

        <button
          onClick={saveConfigs}
          disabled={saving || !hasChanges}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            hasChanges
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  )
}
